/**
 * ActionPlanSummary
 * Shared component that renders the Action Plan (Today / This Week / Longer)
 * from a profile's actionsState + an ActionDefDto catalogue.
 *
 * Props:
 *  actionsState  – AdminActionDto[] from the active profile
 *  actionDef     – ActionDefDto | null (catalogue of action definitions)
 *  showRankBtns  – when true, render ▲▼ rank buttons (default false)
 *  onGroupPress  – called when the green schedule/nav button is pressed;
 *                  receives the group id string ('1'–'4') and row context
 *  onRankMove    – called when ▲/▼ is pressed; receives (group, mvar, action, 'up'|'down')
 */

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import type { AdminActionDto } from '../../types/api/appProfile';
import type { ActionDefDto } from '../../storage/ActionDefStorage';
import type { PrescriptionEventDto, ReferralEventDto } from '../../storage/WellCheckStorage';

// ── Constants ────────────────────────────────────────────────────────────────

const GROUP_TAB_INFO: Record<string, { icon: string; name: string }> = {
  '1': { icon: '⭐', name: 'Goals' },
  '2': { icon: '🛠️', name: 'Concerns' },
  '3': { icon: '🤝', name: 'Advocate' },
  '4': { icon: '🩺', name: 'Provider' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function isDateEventType(actionId: string, actionDef: ActionDefDto | null): boolean {
  if (actionId === '1') return true;
  const at = actionDef?.actionTypes.find(
    t => t.action.toLowerCase() === actionId.toLowerCase()
  );
  if (!at) return false;
  if (at.name.toLowerCase() === 'event') return true;
  if (at.schedule.length === 1 && at.schedule[0].text.toLowerCase().includes('date')) return true;
  return false;
}

function getActionType(actionId: string, actionDef: ActionDefDto | null) {
  if (!actionDef) return null;
  return actionDef.actionTypes.find(
    t => t.action.toLowerCase() === actionId.toLowerCase()
  ) ?? null;
}

function getGroupMvarText(group: string, mvarid: string, actionDef: ActionDefDto | null): string {
  if (!actionDef) return mvarid;
  const ag = actionDef.actionGroups.find(g => g.group === group);
  return ag?.actionMVars.find(
    m => m.mvar.toLowerCase() === mvarid.toLowerCase()
  )?.text ?? mvarid;
}

function getActionName(actionId: string, actionDef: ActionDefDto | null): string {
  if (!actionDef) return actionId;
  return actionDef.actionTypes.find(
    t => t.action.toLowerCase() === actionId.toLowerCase()
  )?.name ?? actionId;
}

function getMedicalTrackLabel(
  mvar: string,
  prescriptionEvents: PrescriptionEventDto[],
  referralEvents: ReferralEventDto[],
): string | null {
  const key = (mvar || '').trim();
  if (!key) return null;

  if (key.toLowerCase().startsWith('prescription_')) {
    const id = key.slice('prescription_'.length);
    const match = prescriptionEvents.find(e => (e.id || '').trim() === id);
    if (!match) return null;
    const name = (match.name || '').trim() || 'Prescription';
    const freq = (match.freq || '').trim();
    return freq ? `${name} - ${freq}` : name;
  }

  if (key.toLowerCase().startsWith('referral_')) {
    const id = key.slice('referral_'.length);
    const match = referralEvents.find(e => (e.id || '').trim() === id);
    if (!match) return null;
    const to = (match.to || '').trim();
    const reason = (match.for || '').trim();
    if (to && reason) return `Referral to ${to} for ${reason}`;
    if (to) return `Referral to ${to}`;
    if (reason) return `Referral for ${reason}`;
    return 'Referral';
  }

  return null;
}

function getOutcomeDisplayText(
  actionId: string,
  outcomeValue: string,
  actionDef: ActionDefDto | null,
): string {
  const raw = (outcomeValue || '').trim();
  if (!raw) return 'No outcome recorded';
  if (!actionDef) return raw;

  const type = actionDef.actionTypes.find(
    t => t.action.toLowerCase() === actionId.toLowerCase()
  );
  if (!type) return raw;

  const match = type.outcome.find(o => (o.value || '').trim().toLowerCase() === raw.toLowerCase());
  return match?.text?.trim() || raw;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ActionPlanSummaryProps {
  actionsState: AdminActionDto[];
  actionDef: ActionDefDto | null;
  prescriptionEvents?: PrescriptionEventDto[];
  referralEvents?: ReferralEventDto[];
  mode?: 'all' | 'todayOnly';
  showTodayHeader?: boolean;
  todayEmptyText?: string;
  showRankBtns?: boolean;
  forceSingleRowOnMobile?: boolean;
  onGroupPress?: (group: string, item?: AdminActionDto) => void;
  onRankSwap?: (
    groupA: string, mvarA: string, actionA: string,
    groupB: string, mvarB: string, actionB: string,
  ) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ActionPlanSummary: React.FC<ActionPlanSummaryProps> = ({
  actionsState,
  actionDef,
  prescriptionEvents = [],
  referralEvents = [],
  mode = 'all',
  showTodayHeader = true,
  todayEmptyText = 'No actions due today',
  showRankBtns = false,
  forceSingleRowOnMobile = false,
  onGroupPress,
  onRankSwap,
}) => {
  const [showAllFinished, setShowAllFinished] = useState(false);

  const toNumericRank = (rank: string | number | undefined, fallback: number): number => {
    const parsed = Number(rank);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  if (actionsState.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>
          {mode === 'todayOnly'
            ? todayEmptyText
            : 'No actions selected yet\nUse Manage Actions tab to select actions'}
        </Text>
      </View>
    );
  }

  // Normalise ranks and sort — exclude finished (status='-1') actions
  const ranked = [...actionsState]
    .filter(a => a.status !== '-1')
    .map((a, i) => ({ ...a, rank: toNumericRank(a.rank, actionsState.length + i + 1) }))
    .sort((a, b) => (a.rank as number) - (b.rank as number));

  // Finished actions — reverse-chronological by completed timestamp
  const finishedActions: (AdminActionDto & { rank: number })[] = actionsState
    .filter(a => a.status === '-1')
    .sort((a, b) => (b.completed || '').localeCompare(a.completed || ''))
    .map((a, i) => ({ ...a, rank: i + 1 }));

  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const bucketDays = (a: AdminActionDto): number | null => {
    if (isDateEventType(a.action, actionDef)) {
      if (!a.dueDate) return null;
      const parts = a.dueDate.substring(0, 10).split('-').map(Number);
      const eventMidnight = new Date(parts[0], parts[1] - 1, parts[2]);
      return Math.round((eventMidnight.getTime() - todayMidnight.getTime()) / 86400000);
    }
    const at = getActionType(a.action, actionDef);
    const schedVal = a.schedule || at?.default || '';
    if (!schedVal) return null;
    const days = parseInt(schedVal, 10);
    if (isNaN(days) || days === -2) return null; // -2 = Open-ended → Longer bucket
    return days;
  };

  const isTodayBucket = (a: AdminActionDto) => {
    const d = bucketDays(a);
    if (d === null) return false;
    return isDateEventType(a.action, actionDef) ? d <= 0 : d <= 1;
  };
  const isWeekBucket = (a: AdminActionDto) => {
    const d = bucketDays(a);
    if (d === null) return false;
    return isDateEventType(a.action, actionDef) ? d >= 1 && d <= 7 : d > 1 && d <= 7;
  };
  const isLaterBucket = (a: AdminActionDto) => {
    const d = bucketDays(a);
    return d === null || d > 7;
  };

  const todayActions  = ranked.filter(a => isTodayBucket(a));
  const weekActions   = ranked.filter(a => isWeekBucket(a));
  const laterActions  = ranked.filter(a => isLaterBucket(a));

  const renderPlanRow = (
    a: AdminActionDto & { rank: number },
    bucketItems: (AdminActionDto & { rank: number })[],
    bucketIdx: number,
    isFinishedGroup: boolean = false,
  ) => {
    const tabInfo = GROUP_TAB_INFO[a.group] ?? { icon: '📋', name: a.group };
    const mvarKey = (a.mvar || '').toLowerCase();
    const isWellCheckTracked = mvarKey.startsWith('wellcheck_');
    const sourceLabel = (((a as any).source ?? (a as any).Source ?? '') as string).trim();
    const mvarName   = isWellCheckTracked
      ? (sourceLabel || 'Well-Check')
      : (getMedicalTrackLabel(a.mvar, prescriptionEvents, referralEvents)
        ?? getGroupMvarText(a.group, a.mvar, actionDef));
    const actionName = getActionName(a.action, actionDef);
    const at         = getActionType(a.action, actionDef);
    const isDate     = isDateEventType(a.action, actionDef);

    const effectiveSchedule = !isDate && !a.schedule ? (at?.default ?? '') : a.schedule;
    const scheduleText = isDate
      ? (a.dueDate || '—')
      : (() => {
          const opts = at?.schedule ?? [];
          const match = opts.find(o => o.value === effectiveSchedule);
          return match ? match.text : '—';
        })();

    const disableUp   = bucketIdx <= 0;
    const disableDown = bucketIdx >= bucketItems.length - 1;
    const outcomeText = getOutcomeDisplayText(a.action, a.outcome, actionDef);

    return (
      <View style={[styles.planRow, forceSingleRowOnMobile && styles.planRowSingleLineMobile]}>
        {/* Cell 1 – group icon · mvar name (tappable) */}
        <TouchableOpacity
          style={[
            styles.planCell1,
            Platform.OS !== 'web' && !forceSingleRowOnMobile && styles.planCell1Mobile,
            forceSingleRowOnMobile && styles.planCell1SingleLineMobile,
          ]}
          onPress={() => onGroupPress?.(a.group, a)}
          activeOpacity={0.7}
        >
          <Text style={styles.planGroupIcon}>{tabInfo.icon}</Text>
          <Text style={styles.planMvarName} numberOfLines={forceSingleRowOnMobile ? undefined : 2}>{mvarName}</Text>
        </TouchableOpacity>
        {/* Cell 2 – action name | schedule/nav btn | optional rank btns */}
        <View
          style={[
            styles.planCell2,
            Platform.OS !== 'web' && !forceSingleRowOnMobile && styles.planCell2Mobile,
            forceSingleRowOnMobile && styles.planCell2SingleLineMobile,
          ]}
        >
          <TouchableOpacity
            style={styles.planCell2A}
            onPress={() => onGroupPress?.(a.group, a)}
            activeOpacity={0.7}
          >
            <Text style={styles.planActionName} numberOfLines={forceSingleRowOnMobile ? undefined : 2}>{actionName}</Text>
          </TouchableOpacity>
          <View style={[styles.planCell2B, isFinishedGroup && styles.planCell2BFinished]}>
            {isFinishedGroup ? (
              <Text style={styles.planOutcomeText} numberOfLines={3}>{outcomeText}</Text>
            ) : (
              <TouchableOpacity
                style={styles.planTabNavBtn}
                onPress={() => onGroupPress?.(a.group, a)}
                activeOpacity={0.75}
              >
                <Text style={styles.planTabNavBtnText} numberOfLines={1}>{scheduleText}</Text>
              </TouchableOpacity>
            )}
          </View>
          {showRankBtns && (
            <View style={styles.planCell2C}>
              <View style={styles.planRankBtns}>
                <TouchableOpacity
                  style={[styles.planRankBtn, disableUp && styles.planRankBtnDisabled]}
                  onPress={() => {
                    const nb = bucketItems[bucketIdx - 1];
                    if (nb) onRankSwap?.(a.group, a.mvar, a.action, nb.group, nb.mvar, nb.action);
                  }}
                  disabled={disableUp}
                >
                  <Text style={[styles.planRankBtnText, disableUp && styles.planRankBtnTextDisabled]}>▲</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.planRankBtn, disableDown && styles.planRankBtnDisabled]}
                  onPress={() => {
                    const nb = bucketItems[bucketIdx + 1];
                    if (nb) onRankSwap?.(a.group, a.mvar, a.action, nb.group, nb.mvar, nb.action);
                  }}
                  disabled={disableDown}
                >
                  <Text style={[styles.planRankBtnText, disableDown && styles.planRankBtnTextDisabled]}>▼</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    );
  };

  const renderPlanGroup = (
    title: string,
    icon: string,
    items: (AdminActionDto & { rank: number })[],
    accentColor: string,
  ) => {
    if (items.length === 0) return null;
    const isFinishedGroup = title === 'Finished';
    const hasMoreThanFive = isFinishedGroup && items.length > 5;
    const visibleItems = isFinishedGroup && !showAllFinished ? items.slice(0, 5) : items;

    return (
      <View style={styles.planGroupCard} key={title}>
        <View style={[styles.planGroupHeader, { borderLeftColor: accentColor, backgroundColor: accentColor + '18' }]}>
          <Text style={[styles.planGroupHeaderText, { color: accentColor }]}>{icon}  {title}</Text>
        </View>
        <View style={styles.planGroupRows}>
          {visibleItems.map((a, idx) => (
            <View
              key={`${a.group}-${a.mvar}-${a.action}`}
              style={[styles.planRowWrapper, idx < visibleItems.length - 1 && styles.planRowDivider]}
            >
              {renderPlanRow(a, visibleItems, idx, isFinishedGroup)}
            </View>
          ))}
          {hasMoreThanFive && (
            <TouchableOpacity
              style={styles.finishedToggleBtn}
              onPress={() => setShowAllFinished(prev => !prev)}
              activeOpacity={0.75}
            >
              <Text style={styles.finishedToggleBtnText}>
                {showAllFinished ? 'Show first 5 ▲' : 'Show all ▼'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const hasAny = todayActions.length > 0 || weekActions.length > 0 || laterActions.length > 0 || finishedActions.length > 0;

  if (mode === 'todayOnly') {
    if (todayActions.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>{todayEmptyText}</Text>
        </View>
      );
    }

    if (showTodayHeader) {
      return (
        <View style={styles.planGroupsContainer}>
          {renderPlanGroup("Today's Actions", '🌱', todayActions, '#059669')}
        </View>
      );
    }

    return (
      <View style={styles.planRowsOnlyContainer}>
        {todayActions.map((a, idx) => (
          <View
            key={`${a.group}-${a.mvar}-${a.action}`}
            style={[styles.planRowWrapper, idx < todayActions.length - 1 && styles.planRowDivider]}
          >
            {renderPlanRow(a, todayActions, idx, false)}
          </View>
        ))}
      </View>
    );
  }

  return (
    <View style={styles.planGroupsContainer}>
      {renderPlanGroup("Today's Actions", '🌱', todayActions, '#059669')}
      {renderPlanGroup('This Week', '🦋', weekActions, '#10b981')}
      {renderPlanGroup('Longer', '🗓️', laterActions, '#10b981')}
      {renderPlanGroup('Finished', '✅', finishedActions, '#64748b')}
      {!hasAny && (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>
            No actions scheduled yet{'\n'}Add due dates or schedules to your actions
          </Text>
        </View>
      )}
    </View>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  emptyContainer: {
    padding: 20,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
    lineHeight: 22,
  },
  planGroupsContainer: {
    gap: 14,
  },
  planRowsOnlyContainer: {
    paddingHorizontal: 4,
    paddingTop: 2,
  },
  planGroupCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    overflow: 'hidden' as const,
    // iOS shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    // Android elevation
    elevation: 2,
  },
  planGroupHeader: {
    borderLeftWidth: 4,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  planGroupHeaderText: {
    fontSize: 15,
    fontWeight: '700' as const,
    letterSpacing: 0.2,
  },
  planGroupRows: {
    paddingHorizontal: 4,
    paddingBottom: 4,
    paddingTop: 2,
  },
  planRowWrapper: {
    paddingHorizontal: 6,
  },
  planRowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  planRow: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    alignItems: 'stretch',
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  planRowSingleLineMobile: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  planCell1: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingRight: 6,
  },
  planCell1Mobile: {
    flex: undefined,
    paddingRight: 0,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  planCell1SingleLineMobile: {
    flex: 1,
    paddingRight: 6,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
  planGroupIcon: {
    fontSize: 16,
    width: 22,
    textAlign: 'center' as const,
    flexShrink: 0,
  },
  planMvarName: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '600' as const,
    flex: 1,
  },
  planCell2: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderLeftWidth: Platform.OS === 'web' ? 1 : 0,
    borderLeftColor: '#e2e8f0',
    paddingLeft: Platform.OS === 'web' ? 8 : 0,
  },
  planCell2Mobile: {
    flex: undefined,
    borderLeftWidth: 0,
    paddingLeft: 0,
    paddingTop: 6,
  },
  planCell2SingleLineMobile: {
    flex: 1,
    borderLeftWidth: 1,
    paddingLeft: 8,
    paddingTop: 0,
  },
  planCell2A: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'flex-start' as const,
    paddingRight: 4,
  },
  planCell2B: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  },
  planCell2BFinished: {
    alignItems: 'flex-start' as const,
  },
  planCell2C: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'flex-end' as const,
  },
  planActionName: {
    fontSize: 13,
    color: '#475569',
  },
  planOutcomeText: {
    fontSize: 13,
    color: '#475569',
    lineHeight: 18,
  },

  planTabNavBtn: {
    backgroundColor: '#10b981',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minWidth: 60,
    maxWidth: 100,
  },
  planTabNavBtnText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600' as const,
    textAlign: 'center' as const,
  },
  planRankBtns: {
    flexDirection: 'row',
    gap: 2,
  },
  planRankBtn: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#cbd5e1',
  },
  planRankBtnDisabled: {
    opacity: 0.3,
  },
  planRankBtnText: {
    fontSize: 11,
    color: '#475569',
    fontWeight: '700' as const,
  },
  planRankBtnTextDisabled: {
    color: '#94a3b8',
  },
  finishedToggleBtn: {
    marginTop: 6,
    paddingVertical: 8,
    alignItems: 'center' as const,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
  },
  finishedToggleBtnText: {
    fontSize: 12,
    color: '#334155',
    fontWeight: '600' as const,
  },
});
